import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { date } from '../lib/format';
import { hasArea, isAdmin } from '../lib/permissions';
import {
  describeMilestone,
  REMINDER_REASON_LABELS,
  REMINDER_STATUS_LABELS,
  type ComplianceReminder,
  type ReminderReason,
  type ReminderRunSummary,
  type ReminderStatus,
} from '../lib/compliance-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  Td,
  Th,
  type BadgeTone,
} from '../components/ui';

const STATUS_TONES: Record<ReminderStatus, BadgeTone> = {
  SENT: 'ok',
  NOT_SENT: 'danger',
  FAILED: 'warn',
  SUPERSEDED: 'neutral',
};

function ozetMetni(s: ReminderRunSummary): string {
  if (s.candidates === 0) return 'Şu an gönderilecek hatırlatma yok.';
  const parcalar = [`${s.candidates} hatırlatma: ${s.sent} gönderildi`];
  if (s.notSent > 0) parcalar.push(`${s.notSent} gönderilemedi`);
  if (s.failed > 0) parcalar.push(`${s.failed} hata`);
  return parcalar.join(', ') + (s.reason ? ` — ${REMINDER_REASON_LABELS[s.reason]}` : '');
}

/**
 * HATIRLATMA GÜNLÜĞÜ (M8b-3).
 *
 * ⚠⚠ GÖNDERİLEMEYEN HATIRLATMA EN ÜSTTE ve kırmızı. Muhasebecisi olmayan bir
 * operatör için en tehlikeli durum "hatırlatma gelmedi, demek ki iş yok"
 * sanmaktır; e-posta ayarı eksikse bunu söyleyecek tek yer bu ekran.
 *
 * ⚠ Uyarı yalnız SON denemenin gününe bakar: süresi geçip takvimden düşmüş bir
 * kalemin eski "gönderilemedi" satırı bugünün sorunu değildir.
 */
export function ComplianceReminders(props: { labels: ReadonlyMap<string, string> }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const ayarlaraGidebilir = hasArea(user, 'settings');
  const qc = useQueryClient();
  const [ozet, setOzet] = useState<ReminderRunSummary | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const gunluk = useQuery({
    queryKey: ['compliance', 'reminders'],
    queryFn: () => api.get<ComplianceReminder[]>('/compliance/reminders?limit=50'),
  });

  const calistir = useMutation({
    mutationFn: () => api.post<ReminderRunSummary>('/compliance/reminders/run'),
    onSuccess: async (s) => {
      setOzet(s);
      await qc.invalidateQueries({ queryKey: ['compliance', 'reminders'] });
    },
    onError: (e: unknown) => setHata(errorMessage(e)),
  });

  const satirlar = gunluk.data ?? [];
  const sonDeneme = satirlar.reduce<string | null>(
    (enGec, r) => (enGec === null || r.lastAttemptOn > enGec ? r.lastAttemptOn : enGec),
    null,
  );
  const ulasmayan = satirlar.filter(
    (r) => r.lastAttemptOn === sonDeneme && (r.status === 'NOT_SENT' || r.status === 'FAILED'),
  );
  const sebepler = [
    ...new Set(ulasmayan.map((r) => r.reason).filter((x): x is ReminderReason => x !== null)),
  ];

  return (
    <Card className="mt-4">
      <CardHeader
        title="Hatırlatmalar"
        description="Günde iki kez (07:00 ve 13:00, Brüksel saati) çalışır: son güne 30, 14, 7, 2 gün kala ve son gün tek bir özet e-postası gönderir. Beyanı sizin yerinize VERMEZ."
      />

      {ulasmayan.length > 0 && (
        <div className="space-y-1 border-b border-[var(--c-border)] px-4 py-3 text-sm text-[var(--c-danger)]">
          <p>
            <strong>Son hatırlatmalar size ULAŞMADI</strong> ({date(sonDeneme)} · {ulasmayan.length}{' '}
            yükümlülük). Takvimi bu ekrandan takip edin.
          </p>
          {sebepler.map((s) => (
            <p key={s} className="text-xs">
              {REMINDER_REASON_LABELS[s]}
            </p>
          ))}
          {ayarlaraGidebilir && (
            <Link to="/settings" className="text-xs underline">
              Ayarlara git
            </Link>
          )}
        </div>
      )}

      {admin && (
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--c-border)] px-4 py-3">
          <Button
            variant="secondary"
            disabled={calistir.isPending}
            onClick={() => {
              setHata(null);
              setOzet(null);
              calistir.mutate();
            }}
          >
            Şimdi çalıştır
          </Button>
          {calistir.isPending && <Spinner />}
          {ozet && <span className="text-xs text-[var(--c-ink-soft)]">{ozetMetni(ozet)}</span>}
          {hata && <span className="text-xs text-[var(--c-danger)]">{hata}</span>}
        </div>
      )}

      {gunluk.isPending ? (
        <div className="px-4 py-6">
          <Spinner />
        </div>
      ) : gunluk.isError ? (
        <ErrorState message={errorMessage(gunluk.error)} />
      ) : satirlar.length === 0 ? (
        <EmptyState
          title="Henüz hatırlatma yok"
          description="Bir son güne 30 gün kalınca ilk hatırlatma burada görünür."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Deneme</Th>
              <Th>Yükümlülük</Th>
              <Th>Hatırlatma</Th>
              <Th>Durum</Th>
              <Th>Açıklama</Th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((r) => (
              <tr key={r.id}>
                <Td className="text-xs">
                  {date(r.lastAttemptOn)}
                  {r.attempts > 1 && (
                    <>
                      <br />
                      <span className="text-[var(--c-ink-soft)]">{r.attempts}. deneme</span>
                    </>
                  )}
                </Td>
                <Td>
                  <span className="text-sm">{props.labels.get(r.obligationKind) ?? r.obligationKind}</span>{' '}
                  <span className="font-mono text-xs">{r.periodKey}</span>
                  <br />
                  <span className="text-xs text-[var(--c-ink-soft)]">
                    son gün {date(r.dueDate)}
                    {r.dueDateOverridden ? ' · elle girilen tarih' : ''}
                  </span>
                </Td>
                <Td className="text-xs">
                  {describeMilestone(r.milestone)}
                  {r.collapsedCount > 0 && (
                    <>
                      <br />
                      <span className="text-[var(--c-ink-soft)]">önceki {r.collapsedCount} hatırlatma atlandı</span>
                    </>
                  )}
                </Td>
                <Td>
                  <Badge tone={STATUS_TONES[r.status]}>{REMINDER_STATUS_LABELS[r.status]}</Badge>
                </Td>
                <Td className="text-xs">
                  {r.reason && <span>{REMINDER_REASON_LABELS[r.reason]}</span>}
                  {r.detail && (
                    <>
                      <br />
                      <span className="text-[var(--c-ink-soft)]">{r.detail}</span>
                    </>
                  )}
                  {r.status === 'SENT' && r.recipient && (
                    <span className="text-[var(--c-ink-soft)]">→ {r.recipient}</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
